import { Phone, Calendar } from 'lucide-react';
import { useState, useEffect, useCallback, memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CalendarPopup from './CalendarPopup';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../hooks/useTheme';
import logoVersionWeb from '../assets/logo_versionweb_noir.svg';
import logoVersionWebBlanc from '../assets/logo_versionweb_blanc.svg';

const scrollToSection = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    const offset = 80;
    const y = element.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }
};

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const NavButton = memo(({
  onClick,
  children
}: {
  onClick: () => void;
  children: React.ReactNode
}) => (
  <button
    onClick={onClick}
    className="text-warm-gray-700 dark:text-warm-gray-300 hover:text-aurore-dark font-medium transition-colors duration-300"
  >
    {children}
  </button>
));

NavButton.displayName = 'NavButton';

const PhoneLink = memo(({ className, showLabel = true }: { className?: string; showLabel?: boolean }) => (
  <a 
    href="tel:+33750717728" 
    className={className}
    aria-label="Appeler Aurore RH"
  >
    <Phone className="w-4 h-4" aria-hidden="true" />
    {showLabel && <span className="text-sm font-medium">07 50 71 77 28</span>}
  </a>
));

PhoneLink.displayName = 'PhoneLink';

function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);
  const { resolvedTheme } = useTheme();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 250);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleOpenCalendly = useCallback(() => setIsCalendlyOpen(true), []);
  const handleCloseCalendly = useCallback(() => setIsCalendlyOpen(false), []);
  
  const handleScrollToAbout = useCallback(() => {
    if (isHomePage) {
      scrollToSection('about');
    }
  }, [isHomePage]);
  
  const handleScrollToServices = useCallback(() => {
    if (isHomePage) {
      scrollToSection('services');
    }
  }, [isHomePage]);
  
  const handleScrollToTestimonials = useCallback(() => {
    if (isHomePage) {
      scrollToSection('testimonials');
    }
  }, [isHomePage]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-white dark:bg-warm-gray-900 shadow-lg py-4 md:py-5'
            : 'bg-aurore-pale dark:bg-warm-gray-900 py-5 md:py-6'
        }`}
      >
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link 
            to="/" 
            onClick={scrollToTop}
            className="flex items-center focus:outline-none rounded-lg"
            aria-label="Retour à l'accueil"
          >
            <div className="hidden lg:flex items-center h-12 md:h-16 w-[120px] md:w-[160px]">
              <img
                src={resolvedTheme === 'dark' ? logoVersionWebBlanc : logoVersionWeb}
                alt="Aurore RH - Consultante RH et Droit Social"
                className="max-h-full max-w-full object-contain object-left"
                loading="eager"
              />
            </div>
          </Link>

          <div className="flex items-center gap-2 md:gap-0 md:space-x-2 flex-1 md:flex-none justify-end">
            <PhoneLink
              className="md:hidden flex items-center justify-center space-x-1.5 bg-aurore-light/40 text-aurore-dark px-3 py-3 rounded-xl hover:bg-aurore-medium hover:text-white transition-all duration-300 flex-1"
              showLabel
            />

            <div className="hidden md:flex items-center space-x-8">
              {isHomePage ? (
                <>
                  <NavButton onClick={handleScrollToAbout}>À propos</NavButton>
                  <NavButton onClick={handleScrollToServices}>Services</NavButton>
                  <NavButton onClick={handleScrollToTestimonials}>Témoignages</NavButton>
                </>
              ) : (
                <>
                  <Link to="/#about" className="text-warm-gray-700 dark:text-warm-gray-300 hover:text-aurore-dark font-medium transition-colors duration-300">
                    À propos
                  </Link>
                  <Link to="/#services" className="text-warm-gray-700 dark:text-warm-gray-300 hover:text-aurore-dark font-medium transition-colors duration-300">
                    Services
                  </Link>
                  <Link to="/#testimonials" className="text-warm-gray-700 dark:text-warm-gray-300 hover:text-aurore-dark font-medium transition-colors duration-300">
                    Témoignages
                  </Link>
                </>
              )}

              <PhoneLink
                className="flex items-center space-x-2 text-warm-gray-700 dark:text-warm-gray-300 hover:text-aurore-dark transition-colors duration-300"
              />

              <ThemeToggle />

              <button
                onClick={handleOpenCalendly}
                className="bg-aurore-dark text-white px-6 py-3 rounded-2xl hover:bg-aurore-medium hover:shadow-md transition-all duration-500 flex items-center space-x-2 focus:outline-none focus:ring-2 focus:ring-aurore-medium/40 focus:ring-offset-2"
                aria-label="Prendre un rendez-vous"
              >
                <Calendar className="w-4 h-4" aria-hidden="true" />
                <span className="font-medium">Échanger</span>
              </button>
            </div>

            <button
              onClick={handleOpenCalendly}
              className="md:hidden flex items-center justify-center space-x-1.5 bg-aurore-dark text-white px-3 py-3 rounded-xl hover:bg-aurore-medium transition-all duration-300 flex-1 focus:outline-none focus:ring-2 focus:ring-aurore-medium/40 focus:ring-offset-2"
              aria-label="Prendre un rendez-vous"
            >
              <Calendar className="w-4 h-4" aria-hidden="true" />
              <span className="text-sm font-medium whitespace-nowrap">
                Échanger
              </span>
            </button>
          </div>
        </div>
      </nav>

      <CalendarPopup
        isOpen={isCalendlyOpen}
        onClose={handleCloseCalendly}
      />
    </>
  );
}

export default memo(Navigation);
